/** @format */

import { useIsFetching } from "@tanstack/react-query";
import { type CSSProperties, useMemo } from "react";

import { useRevalidationRacerEnabled } from "@/hooks/useStableFeatureFlags";

/** Per-car configuration for the multi-car variant. */
export type RacerCar = {
  /** Body color. Falls back to the component-level `color` prop. */
  color?: string;
  /** Number rendered on the door. Defaults to "88". */
  number?: string;
  /**
   * Starting position along the lap as a fraction in [0, 1). When omitted,
   * cars are spaced evenly based on their index in the `cars` array.
   */
  startOffset?: number;
};

export type RevalidatingRacerProps = {
  /** When true, the car(s) are visible and looping the viewport edge. */
  active: boolean;
  /** Full-lap duration in ms. Default 12_000. */
  durationMs?: number;
  /** Car length in px. Height is derived from the 5:2 aspect. Default 100. */
  size?: number;
  /** Inset from the viewport edges, in px. Default 2. */
  marginPx?: number;
  /** Travel direction. Default "ccw". */
  direction?: "cw" | "ccw";
  /** Body color used when a single car is rendered. Default "#38bdf8". */
  color?: string;
  /** Number rendered on the door when a single car is rendered. Default "88". */
  number?: string;
  /**
   * When provided, renders one car per entry. Each car shares the same
   * path / duration / size / direction, but gets its own color, number,
   * and (auto-computed if omitted) start offset so they space out around
   * the track. If omitted, a single car is rendered using `color`/`number`.
   */
  cars?: RacerCar[];
  /**
   * Corner radius of the path, in px. When omitted, uses the tightest
   * radius that still keeps the car fully on-screen
   * (= min(topBottomInset, leftRightInset)).
   */
  cornerRadius?: number;
  /**
   * Base z-index. Each car in a multi-car render gets `zIndex + index` so
   * overlaps layer predictably. Default 2147483000 (one below browser
   * chrome reserved).
   */
  zIndex?: number;
};

const CAR_ASPECT = 749.22 / 209.59; // matches the artwork SVG viewBox

/**
 * A tiny stock car that laps the viewport perimeter while data is
 * revalidating. Purely decorative (aria-hidden, pointer-events: none).
 *
 * Uses CSS `offset-path` with an `inset()` shape so the browser handles the
 * rectangular path; `offset-rotate: auto` keeps the car's nose aligned with
 * the direction of travel around the corners.
 */
export function RevalidatingRacer({
  active,
  durationMs = 12_000,
  size = 100,
  marginPx = 2,
  direction = "ccw",
  color = "#38bdf8",
  number = "88",
  cars,
  cornerRadius,
  zIndex = 2147483000,
}: RevalidatingRacerProps) {
  if (!active) return null;

  const list: RacerCar[] = cars && cars.length > 0 ? cars : [{ color, number }];
  const total = list.length;

  return (
    <>
      {list.map((car, index) => {
        const resolvedOffset = car.startOffset ?? (total > 1 ? index / total : 0);
        // The cars array is a fixed lap field — order is stable, so the
        // index is a legitimate key. (Index + startOffset disambiguates
        // legitimate duplicates of color/number.)
        const key = `${index}-${resolvedOffset}`;
        return (
          <SingleCarRacer
            key={key}
            durationMs={durationMs}
            size={size}
            marginPx={marginPx}
            direction={direction}
            color={car.color ?? color}
            number={car.number ?? number}
            startOffset={resolvedOffset}
            cornerRadius={cornerRadius}
            zIndex={zIndex + index}
          />
        );
      })}
    </>
  );
}

type SingleCarRacerProps = {
  durationMs: number;
  size: number;
  marginPx: number;
  direction: "cw" | "ccw";
  color: string;
  number: string;
  startOffset: number;
  cornerRadius: number | undefined;
  zIndex: number;
};

function SingleCarRacer({
  durationMs,
  size,
  marginPx,
  direction,
  color,
  number,
  startOffset,
  cornerRadius,
  zIndex,
}: SingleCarRacerProps) {
  const isCCW = direction === "ccw";

  const width = size;
  const height = Math.round(size / CAR_ASPECT);

  const topBottomInset = marginPx + height / 2;
  const leftRightInset = marginPx + height / 2;
  const resolvedCornerRadius = cornerRadius ?? Math.min(topBottomInset, leftRightInset);

  // A negative animation-delay makes the lap *start* partway through —
  // this is what lets us space multiple cars evenly around the track
  // without keyframe tricks.
  const animationDelay = -startOffset * durationMs;

  const style: CSSProperties = {
    top: 0,
    left: 0,
    width,
    height,
    color,
    offsetPath: `inset(${topBottomInset}px ${leftRightInset}px round ${resolvedCornerRadius}px)`,
    offsetRotate: isCCW ? "auto 180deg" : "auto 180deg",
    offsetAnchor: "bottom",
    transform: isCCW ? "scaleX(-1)" : "",
    animation: `galaxie-revalidating-lap ${durationMs}ms linear infinite ${isCCW ? "reverse" : "normal"}`,
    animationDelay: `${animationDelay}ms`,
  };

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed motion-reduce:hidden"
      style={{ ...style, zIndex }}
    >
      <StockCarSilhouette number={number} />
    </div>
  );
}

/**
 * Side profile of a stock car. The source artwork has its nose on the
 * LEFT; we wrap it in a horizontal flip so the rendered car faces RIGHT,
 * which matches the `offset-path` rotation logic in `RevalidatingRacer`.
 */
export function StockCarSilhouette({
  number = "88",
  numberColor = "#0f172a",
  numberStrokeColor = "rgba(255,255,255,0.9)",
  glow = true,
}: {
  number?: string;
  /** Fill colour of the door number. Defaults to near-black. */
  numberColor?: string;
  /** Thin stroke behind the number fill. Defaults to translucent white. */
  numberStrokeColor?: string;
  /** When true (default), adds a currentColor drop-shadow behind the car. */
  glow?: boolean;
} = {}) {
  // Sourced 1:1 from the hand-drawn reference SVG at the project root.
  // Modifications from the original artwork:
  //   - Wrapping <g> flips horizontally (scale(-1,1)) so nose is on right.
  //   - Main body fill → currentColor (tracks the color picker).
  //   - Chassis rocker bar forced near-black so it doesn't track the body.
  //   - "88" overlay added on the door, drawn OUTSIDE the flip so the
  //     text reads left-to-right.
  //
  // Original-SVG anchor points (pre-flip):
  //   Overall SVG       749.22 × 209.59
  //   Left wheel        cx 210.55, cy 155.65, outer r 53.93
  //   Right wheel       cx 641.13, cy 157.98, outer r 51.61
  //   After flip → left wheel becomes front (cx 538.67), right wheel
  //   becomes rear (cx 108.09). Number placed forward toward the front wheel.
  return (
    <svg
      viewBox="0 0 749.22 209.59"
      preserveAspectRatio="xMidYMid meet"
      role="presentation"
      focusable="false"
      className={`block h-full w-full ${glow ? "drop-shadow-[0_2px_8px_currentColor]" : ""}`}
    >
      <g transform="translate(749.22 0) scale(-1 1)">
        {/* Main body silhouette (was black → currentColor) */}
        <path
          fill="currentColor"
          d="M13.65,74.42c.21-1.03,6.34-10.74,6.34-10.74-.47-4.13-1.4-12.39-1.4-12.39l6.31-.94s.94,7.95,1.4,11.93c8.35-1.55,16.7-3.1,25.05-4.65l83.97-9.29c5.7-.63,11.01-3.14,16.14-5.77C221.93,6.46,261.36,2.38,339.62.18c25.06-.7,49.03.68,73.83,3.17,17.53,1.76,33.08,7.24,48.54,15.38,28,14.74,54.25,30.75,80.92,47.89l24.45,2.71c35.48,3.94,99.73,11.74,127.62,25.05,15.78,7.54,43.12,29.08,44.43,42.3l2.48,24.96c.31,3.17-3.47,6.26-6.4,6.23l-35.38-.27c-1.8-32.25-14.78-57.14-46.38-63.9-22.26-6.02-44.43,2.42-58.05,21.15-9.2,12.64-13.58,27.42-16.35,42.86h-306.49c-1.99-34.03-12.94-61.97-46.63-69.86-10.87-1.96-22.02-1.52-32.71,1.24-33.98,10.05-46.38,38.93-46.66,74.11l-128.43-13.76c-9.73-1.04-16.11-7.59-17.64-17.15-3.94-24.48,8.73-47.85,12.9-67.89M348.23,13.67l-3.35,2.4c-.06,17.46-6.21,32.75-.28,50.69l3.62-53.08ZM309.88,65.47c1.52-18.23,3.52-34.1,6.55-51.48l-8.31,1.03-19.57,49.93,21.33.52ZM280.3,65.41l19.61-49.95-14.75,1.73-69.06,48.11,64.19.12ZM448.51,27.95l-19.9-8.42,11.8,45.74,20.66.06-12.57-37.37ZM202.95,65.27l63.26-44.17c-21.28,3.75-39.83,12.27-56.62,24.63l-26.51,19.5,19.88.04ZM501.24,65.06c-14.08-13.03-27.39-21.88-42.7-31.28l10.11,31.35,32.6-.07Z"
        />

        {/* Rocker / chassis detail bar (stays near-black) */}
        <rect
          fill="#0b0d12"
          x="411.8"
          y="30.85"
          width="26.33"
          height="307.56"
          transform="translate(609.58 -240.34) rotate(90)"
        />

        {/* Left-side wheel in the source SVG (→ front wheel after flip) */}
        <path
          fill="#0a0a0a"
          d="M210.55,101.72c29.79,0,53.93,24.15,53.93,53.93,0,29.79-24.15,53.93-53.93,53.93s-53.93-24.15-53.93-53.93c0-29.79,24.15-53.93,53.93-53.93ZM210.53,188.88c18.34,0,33.21-14.87,33.21-33.21s-14.87-33.21-33.21-33.21c-18.34,0-33.21,14.87-33.21,33.21s14.87,33.21,33.21,33.21Z"
        />

        {/* Right-side wheel in the source SVG (→ rear wheel after flip) */}
        <path
          fill="#0a0a0a"
          d="M641.13,106.36c28.5,0,51.61,23.11,51.61,51.61s-23.11,51.61-51.61,51.61c-28.5,0-51.61-23.11-51.61-51.61,0-28.5,23.11-51.61,51.61-51.61ZM641.13,189.39c17.34,0,31.4-14.06,31.4-31.4s-14.06-31.4-31.4-31.4-31.4,14.06-31.4,31.4,14.06,31.4,31.4,31.4Z"
        />

        {/* Tail / exhaust nozzle — dark */}
        <path
          fill="#0a0a0a"
          d="M697.86,197.87c16.72,1.43,49.84-1.97,51.26-10.47.97-5.78-5.45-15.69-8.47-15.72l-40.19-.28-2.59,26.46Z"
        />

        {/* Greenhouse / nose highlights (kept white to read on any body color) */}
        <polygon
          fill="#f8fafc"
          points="216.11 65.29 280.3 65.41 299.91 15.45 285.17 17.18 216.11 65.29"
        />
        <path
          fill="#f8fafc"
          d="M183.08,65.23l19.88.04,63.26-44.17c-21.28,3.75-39.83,12.27-56.62,24.63l-26.51,19.5Z"
        />
        <polygon
          fill="#f8fafc"
          points="461.08 65.33 448.51 27.95 428.61 19.53 440.41 65.27 461.08 65.33"
        />
        <path
          fill="#f8fafc"
          d="M288.55,64.95l21.33.52c1.52-18.23,3.52-34.1,6.55-51.48l-8.31,1.03-19.57,49.93Z"
        />
        <path
          fill="#f8fafc"
          d="M468.64,65.13l32.6-.07c-14.08-13.03-27.39-21.88-42.7-31.28l10.11,31.35Z"
        />
        <path
          fill="#f8fafc"
          d="M344.61,66.75l3.62-53.08-3.35,2.4c-.06,17.46-6.21,32.75-.28,50.69Z"
        />

        {/* Wheel rims (white face) */}
        <path
          fill="#f8fafc"
          d="M210.53,122.47c18.34,0,33.21,14.87,33.21,33.21s-14.87,33.21-33.21,33.21c-18.34,0-33.21-14.87-33.21-33.21s14.87-33.21,33.21-33.21ZM190.56,153.63c-1.05,11.49,7.3,21.09,18.17,22.04,10.8.94,20.3-6.61,21.67-17.16,1.43-10.99-5.6-21.53-18.36-22.84-11.48-1.18-20.58,8.19-21.47,17.97Z"
        />
        <path
          fill="#f8fafc"
          d="M641.13,126.6c17.34,0,31.4,14.06,31.4,31.4s-14.06,31.4-31.4,31.4-31.4-14.06-31.4-31.4,14.06-31.4,31.4-31.4ZM641.13,176.97c10.49,0,19-8.51,19-19s-8.51-19-19-19c-10.49,0-19,8.51-19,19,0,10.49,8.51,19,19,19Z"
        />

        {/* Wheel hubs (dark) */}
        <path
          fill="#0a0a0a"
          d="M212.03,135.66c-11.48-1.18-20.58,8.19-21.47,17.97-1.05,11.49,7.3,21.09,18.17,22.04,10.8.94,20.3-6.61,21.67-17.16,1.43-10.99-5.6-21.53-18.36-22.84Z"
        />
        <circle cx="641.13" cy="157.98" r="19" fill="#0a0a0a" />

        {/* Body crease / panel lines (decorative strokes) */}
        <path
          fill="none"
          stroke="#000"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M267.2,25.14c-17.39,12.32-34.78,24.63-52.15,36.96-1.26.89-2.42,1.92-3.63,2.89"
        />
        <path
          fill="none"
          stroke="#000"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M301.97,11.37c.76,9.36-4.31,17.08-7.33,25.32-3.23,8.84-7.22,17.39-10.71,26.14-.5,1.25-.07,2.87-.07,4.32"
        />
        <path
          fill="none"
          stroke="#000"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M453.38,36.01c3.38,10.38,6.76,20.77,10.14,31.15"
        />
        <path
          fill="none"
          stroke="#000"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M573.64,176.91l-4.35,3.26"
        />
      </g>

      {/* Side number — rendered OUTSIDE the flip so it reads left-to-right.
          Placed forward (closer to the right-side front wheel at cx 538.67). */}
      <text
        x="345"
        y="155"
        textAnchor="middle"
        fontSize="92"
        fontWeight="900"
        fontFamily="'Arial Black', 'Helvetica Neue', system-ui, sans-serif"
        fill={numberColor}
        stroke={numberStrokeColor}
        strokeWidth="2.5"
        paintOrder="stroke fill"
        style={{ letterSpacing: "-4px" }}
      >
        {number}
      </text>
    </svg>
  );
}

export default RevalidatingRacer;

// --- Revalidation wiring -------------------------------------------------

/**
 * Default field for the revalidation indicator. Cars are coloured around the
 * hue wheel so it reads as a bright, moving "something is happening" signal.
 * Numbers rotate through a pool of real Cup-style two-digit numbers.
 */
const REVALIDATION_NUMBER_POOL = [
  "88",
  "24",
  "5",
  "11",
  "9",
  "2",
  "4",
  "48",
  "19",
  "20",
  "22",
  "23",
  "34",
  "41",
  "43",
  "45",
  "47",
  "54",
  "77",
  "99",
];

function buildField(count: number): RacerCar[] {
  return Array.from({ length: count }, (_, i) => ({
    color: `hsl(${(i * 360) / count}, 78%, 58%)`,
    number: REVALIDATION_NUMBER_POOL[i % REVALIDATION_NUMBER_POOL.length],
  }));
}

export type RevalidationIndicatorProps = {
  /** Override the default car count (36). */
  carCount?: number;
  /** Override the default lap duration (18_000 ms). */
  durationMs?: number;
  /** Override the default car length (50 px). */
  size?: number;
  /** Override the default edge margin (0 px). */
  marginPx?: number;
  /** Override the default corner radius (200 px). */
  cornerRadius?: number;
  /** Override the default travel direction (ccw). */
  direction?: "cw" | "ccw";
  /**
   * Override React Query's fetching signal. When omitted, the indicator
   * shows whenever there is at least one **background revalidation** in
   * flight (i.e. a query that already has data is refetching).
   */
  active?: boolean;
};

/**
 * Wires `RevalidatingRacer` to React Query so a field of stock cars appears
 * around the viewport edge whenever a background revalidation is in flight.
 *
 * Initial loads (queries that don't yet have data) are ignored — the racer
 * is specifically a "refreshing existing data" signal, not a "loading".
 *
 * Gated behind the PostHog `revalidation-racer` feature flag. Renders
 * nothing when the flag is disabled, so it's safe to mount at the app
 * root unconditionally.
 */
export function RevalidationIndicator({
  carCount = 36,
  durationMs = 18_000,
  size = 50,
  marginPx = 0,
  cornerRadius = 200,
  direction = "ccw",
  active,
}: RevalidationIndicatorProps = {}) {
  const enabled = useRevalidationRacerEnabled();
  const revalidatingCount = useIsFetching({
    predicate: query => query.state.data !== undefined,
  });
  const cars = useMemo(() => buildField(carCount), [carCount]);

  if (!enabled) return null;

  const shouldShow = active ?? revalidatingCount > 0;

  return (
    <RevalidatingRacer
      active={shouldShow}
      durationMs={durationMs}
      size={size}
      marginPx={marginPx}
      cornerRadius={cornerRadius}
      direction={direction}
      cars={cars}
    />
  );
}
